import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MapPin, Warehouse, PackagePlus, ClipboardList, Truck, Percent, Search, Lock } from "lucide-react";
import Header from "@/components/quote/Header";
import { NumberField } from "@/components/quote/NumberField";
import RateCards from "@/components/quote/RateCards";
import BreakdownPanel from "@/components/quote/BreakdownPanel";
import UpgradeDialog from "@/components/quote/UpgradeDialog";
import { DEFAULT_MODEL } from "@/lib/calc";
import { fetchLocations, fetchRates, saveQuote, emailQuote, calculateEstimate } from "@/lib/api";
import { generateQuotePDF } from "@/lib/pdf";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1664382953403-fc1ac77073a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBsb2dpc3RpY3MlMjB3b3JrZXJzfGVufDB8fHx8MTc5MDY3NTg1Mnww&ixlib=rb-4.1.0&q=85";

const DEFAULTS = {
  location: "60601",
  storageSqft: 2000,
  storageMonths: 1,
  inboundPallets: 40,
  monthlyOrders: 800,
  linesPerOrder: 3,
  repackUnits: 150,
  outboundPallets: 35,
  gaPercent: 10,
  companyName: "",
  contactName: "",
  contactEmail: "",
};

const SectionLabel = ({ icon: Icon, children, step }) => (
  <div className="mb-4 flex items-center gap-2.5">
    <span className="grid h-6 w-6 place-items-center rounded-sm bg-slate-900 text-[11px] font-bold text-white">
      {step}
    </span>
    <Icon className="h-4 w-4 text-blue-600" />
    <h3 className="text-sm font-semibold uppercase tracking-[0.15em] text-slate-700">{children}</h3>
  </div>
);

const Panel = ({ children, delay = 0 }) => (
  <div
    className="gg-rise rounded-sm border border-slate-200 bg-white p-6 shadow-sm"
    style={{ animationDelay: `${delay}ms` }}
  >
    {children}
  </div>
);

export default function Calculator() {
  const { user, isAuthed, openLogin, setUser, refresh } = useAuth();
  const [inputs, setInputs] = useState(DEFAULTS);
  const [cities, setCities] = useState([]);
  const [location, setLocation] = useState(null);
  const [model, setModel] = useState(DEFAULT_MODEL);
  const [uplift, setUplift] = useState(10);
  const [locError, setLocError] = useState("");
  const [saving, setSaving] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const debounceRef = useRef();

  const set = (k) => (v) => setInputs((p) => ({ ...p, [k]: v }));

  const EMPTY = { storage: 0, inbound: 0, pickLabor: 0, repackLabor: 0, handling: 0, outbound: 0, subtotal: 0, ga: 0, total: 0 };

  useEffect(() => {
    document.title = "GloGrid VMI Quoter";
    fetchLocations()
      .then((d) => {
        setCities(d.cities);
        setModel(d.laborModel || DEFAULT_MODEL);
        if (d.laborUplift != null) setUplift(d.laborUplift);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!inputs.location) {
      setLocation(null);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const d = await fetchRates(inputs.location);
        setLocation(d.location);
        setModel(d.laborModel || DEFAULT_MODEL);
        if (d.laborUplift != null) setUplift(d.laborUplift);
        setLocError("");
      } catch (e) {
        setLocation(null);
        setLocError(e?.response?.data?.detail || "Location not found");
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [inputs.location]);

  // Clear a prior result whenever inputs or location change (each calc is metered)
  useEffect(() => {
    setResult(null);
  }, [inputs, location]);

  const breakdown = result || EMPTY;

  const handleCalculate = async () => {
    if (!isAuthed) {
      openLogin();
      return;
    }
    if (!location) {
      toast.error("Please enter a valid US city or zipcode first.");
      return;
    }
    setCalculating(true);
    try {
      const d = await calculateEstimate(inputs);
      setResult(d.breakdown);
      if (d.usage) setUser((prev) => ({ ...(prev || {}), ...d.usage }));
      const rem = d.usage && !d.usage.unlimited ? d.usage.remaining : null;
      toast.success("Estimate calculated", {
        description: rem === null ? undefined : `${rem} free estimate${rem === 1 ? "" : "s"} left this month.`,
      });
    } catch (e) {
      if (e?.response?.status === 402) {
        setShowUpgrade(true);
        refresh();
      } else if (e?.response?.status === 401) {
        openLogin();
      } else {
        toast.error("Could not calculate", { description: e?.response?.data?.detail || "Please try again." });
      }
    } finally {
      setCalculating(false);
    }
  };

  const handleDownload = async () => {
    if (!result) {
      toast.error("Calculate the estimate first.");
      return;
    }
    setSaving(true);
    try {
      const saved = await saveQuote(inputs);
      generateQuotePDF(saved);
      toast.success(`Quote ${saved.reference} generated`, {
        description: "Your PDF quotation has been downloaded.",
      });
    } catch (e) {
      toast.error("Could not generate quote", { description: e?.response?.data?.detail || "Please try again." });
    } finally {
      setSaving(false);
    }
  };

  const handleEmail = async () => {
    if (!result) {
      toast.error("Calculate the estimate first.");
      return;
    }
    if (!inputs.contactEmail || !/\S+@\S+\.\S+/.test(inputs.contactEmail)) {
      toast.error("Add a valid email in Quote Details", {
        description: "Enter the customer email (step 7) to send the quote.",
      });
      return;
    }
    setEmailing(true);
    try {
      const saved = await saveQuote(inputs);
      await emailQuote(saved.reference);
      toast.success(`Quote ${saved.reference} emailed`, {
        description: `Sent to ${inputs.contactEmail}. A copy went to our sales team.`,
      });
    } catch (e) {
      toast.error("Could not email quote", { description: e?.response?.data?.detail || "Please try again." });
    } finally {
      setEmailing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-slate-900">
        <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 gg-grid-bg opacity-[0.06]" />
        <div className="relative mx-auto max-w-7xl px-6 py-14 md:px-12 md:py-20">
          <span className="text-xs font-semibold uppercase tracking-[0.28em] text-blue-400">
            Global Cost · Local Reach
          </span>
          <h1 className="mt-4 max-w-3xl font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl">
            Instant VMI &amp; Warehousing quotes for any US city or zipcode.
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300">
            Configure your storage, inbound, handling and outbound volumes. GloGrid applies live regional
            labor and warehousing rates to build a transparent, itemized quotation in seconds.
          </p>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-6 py-10 md:px-12 md:py-14">
        {/* Usage / auth banner */}
        <div className="mb-6" data-testid="usage-banner">
          {!isAuthed ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-sm border border-slate-200 bg-white px-5 py-4">
              <div className="flex items-center gap-2.5 text-sm text-slate-600">
                <Lock className="h-4 w-4 text-blue-600" />
                Sign in with your email to generate estimates — <span className="font-semibold text-slate-900">3 free every month</span>.
              </div>
              <button
                onClick={openLogin}
                className="rounded-sm bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-600"
                data-testid="banner-signin-btn"
              >
                Sign in
              </button>
            </div>
          ) : user?.unlimited ? (
            <div className="rounded-sm border border-blue-200 bg-blue-50 px-5 py-3 text-sm text-blue-800" data-testid="usage-unlimited">
              Signed in as <span className="font-semibold">{user.email}</span> — unlimited estimates.
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-sm border border-slate-200 bg-white px-5 py-3">
              <span className="text-sm text-slate-600" data-testid="usage-remaining">
                <span className="font-semibold text-slate-900">{user?.remaining ?? 0}</span> of {user?.limit ?? 3} free estimates left this month
              </span>
              {(user?.remaining ?? 0) === 0 && (
                <button
                  onClick={() => setShowUpgrade(true)}
                  className="rounded-sm bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
                  data-testid="banner-upgrade-btn"
                >
                  Upgrade for unlimited
                </button>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          {/* Configurator */}
          <div className="flex flex-col gap-6 lg:col-span-8">
            {/* Location */}
            <Panel delay={0}>
              <SectionLabel icon={MapPin} step={1}>
                Service Location
              </SectionLabel>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="loc-input" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    City or Zipcode
                  </Label>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                      id="loc-input"
                      value={inputs.location}
                      onChange={(e) => set("location")(e.target.value)}
                      placeholder="e.g. 10001 or Chicago"
                      className="h-11 rounded-sm border-slate-300 bg-white pl-9 text-base focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
                      data-testid="location-input"
                    />
                  </div>
                  {locError ? (
                    <span className="text-xs font-medium text-red-500" data-testid="location-error">
                      {locError}
                    </span>
                  ) : location ? (
                    <span className="text-xs text-slate-400" data-testid="location-resolved">
                      Applying rates for <span className="font-semibold text-slate-600">{location.label}</span>
                      {!location.matched && " (regional estimate)"}
                    </span>
                  ) : null}
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Or pick a major metro
                  </Label>
                  <Select value="" onValueChange={(v) => set("location")(v)}>
                    <SelectTrigger
                      className="h-11 rounded-sm border-slate-300 bg-white text-base focus:ring-2 focus:ring-blue-600 focus:ring-offset-0"
                      data-testid="city-select"
                    >
                      <SelectValue placeholder="Select a city…" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {cities.map((c) => (
                        <SelectItem key={c.zip} value={`${c.city}, ${c.state}`} data-testid={`city-option-${c.city}`}>
                          {c.city}, {c.state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {location && (
                <div className="mt-5">
                  <RateCards rates={location.rates} />
                  <p className="mt-2.5 flex items-center gap-1.5 text-xs text-slate-400" data-testid="labor-loading-note">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-500" />
                    Labor rate shown includes the standard ${uplift.toFixed(0)}/hr loading and is used for all handling,
                    picking &amp; repacking costs.
                    {location.custom && <span className="font-semibold text-blue-600"> · Locked rate for this zipcode</span>}
                  </p>
                </div>
              )}
            </Panel>

            {/* Storage */}
            <Panel delay={60}>
              <SectionLabel icon={Warehouse} step={2}>
                Storage of Goods
              </SectionLabel>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <NumberField id="sqft" label="Storage area" value={inputs.storageSqft} onChange={set("storageSqft")} suffix="sqft" step={50} testid="input-sqft" />
                <NumberField id="months" label="Duration" value={inputs.storageMonths} onChange={set("storageMonths")} suffix="months" min={1} testid="input-months" />
              </div>
            </Panel>

            {/* Inbound */}
            <Panel delay={120}>
              <SectionLabel icon={PackagePlus} step={3}>
                Inbound Receiving
              </SectionLabel>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <NumberField id="inbound" label="Inbound pallets" value={inputs.inboundPallets} onChange={set("inboundPallets")} suffix="pallets" hint="Pallets received & put away per month" testid="input-inbound" />
              </div>
            </Panel>

            {/* Handling */}
            <Panel delay={180}>
              <SectionLabel icon={ClipboardList} step={4}>
                Handling · Picking · Repacking
              </SectionLabel>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                <NumberField id="orders" label="Monthly orders" value={inputs.monthlyOrders} onChange={set("monthlyOrders")} suffix="orders" step={10} testid="input-orders" />
                <NumberField id="lines" label="Lines / order" value={inputs.linesPerOrder} onChange={set("linesPerOrder")} suffix="lines" min={1} testid="input-lines" />
                <NumberField id="repack" label="Repack units" value={inputs.repackUnits} onChange={set("repackUnits")} suffix="units" step={10} testid="input-repack" />
              </div>
              <p className="mt-4 text-xs text-slate-400">
                Labor-based: {model.pick_minutes_per_line} min/line picking · {model.repack_minutes_per_unit} min/unit repack, at the location's hourly labor rate.
              </p>
            </Panel>

            {/* Outbound */}
            <Panel delay={240}>
              <SectionLabel icon={Truck} step={5}>
                Outbound / Shipping
              </SectionLabel>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <NumberField id="outbound" label="Outbound pallets" value={inputs.outboundPallets} onChange={set("outboundPallets")} suffix="pallets" hint="Pallets staged & shipped per month" testid="input-outbound" />
              </div>
            </Panel>

            {/* Admin */}
            <Panel delay={300}>
              <SectionLabel icon={Percent} step={6}>
                General & Administrative / Project Management
              </SectionLabel>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">G&A / PM applied on subtotal</span>
                <span className="font-mono-plex text-lg font-semibold text-slate-900" data-testid="ga-value">
                  {inputs.gaPercent}%
                </span>
              </div>
              <Slider
                value={[inputs.gaPercent]}
                onValueChange={([v]) => set("gaPercent")(v)}
                min={0}
                max={25}
                step={0.5}
                className="mt-4"
                data-testid="ga-slider"
              />
              <div className="mt-2 flex justify-between text-xs text-slate-400">
                <span>0%</span>
                <span>25%</span>
              </div>
            </Panel>

            {/* Customer details */}
            <Panel delay={360}>
              <SectionLabel icon={ClipboardList} step={7}>
                Quote Details (for the PDF)
              </SectionLabel>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="company" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Company</Label>
                  <Input id="company" value={inputs.companyName} onChange={(e) => set("companyName")(e.target.value)} placeholder="Acme Corp" className="h-11 rounded-sm border-slate-300 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0" data-testid="input-company" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="contact" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Contact name</Label>
                  <Input id="contact" value={inputs.contactName} onChange={(e) => set("contactName")(e.target.value)} placeholder="Jane Doe" className="h-11 rounded-sm border-slate-300 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0" data-testid="input-contact" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</Label>
                  <Input id="email" type="email" value={inputs.contactEmail} onChange={(e) => set("contactEmail")(e.target.value)} placeholder="jane@acme.com" className="h-11 rounded-sm border-slate-300 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0" data-testid="input-email" />
                </div>
              </div>
            </Panel>
          </div>

          {/* Live breakdown */}
          <div className="lg:col-span-4">
            <BreakdownPanel
              location={location}
              breakdown={breakdown}
              inputs={inputs}
              hasResult={!!result}
              calculating={calculating}
              isAuthed={isAuthed}
              onCalculate={handleCalculate}
              onDownload={handleDownload}
              onEmail={handleEmail}
              saving={saving}
              emailing={emailing}
            />
          </div>
        </div>
      </main>

      <UpgradeDialog open={showUpgrade} onClose={() => setShowUpgrade(false)} email={user?.email} />

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-2 px-6 py-8 text-xs text-slate-400 md:flex-row md:items-center md:px-12">
          <span>© {new Date().getFullYear()} GloGrid · Manufacturing, Warehousing & Global Sourcing</span>
          <div className="flex items-center gap-4">
            <a href="/compare" className="transition-colors hover:text-slate-700" data-testid="footer-compare">Compare locations</a>
            <a href="/lookup" className="transition-colors hover:text-slate-700" data-testid="footer-lookup">Look up quote</a>
            <a href="/sales" className="transition-colors hover:text-slate-700" data-testid="footer-sales">Sales dashboard</a>
            <a href="/admin" className="transition-colors hover:text-slate-700" data-testid="footer-admin">Rate admin</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
